import React, { useCallback, useEffect, useMemo, useState } from 'react';
import aiAutomationApi from '../utils/aiAutomationApi';

const clamp = (value) => Math.min(100, Math.max(0, Math.round(Number(value) || 0)));
const totalScore = (criteria) => criteria.reduce((sum, item) => sum + Math.round(Number(item.weight || 0)), 0);
const weightInputValue = (value) => (value === '' || value === null || value === undefined ? '' : clamp(value));

const normalizeCriteria = (items = []) => (
  Array.isArray(items)
    ? items.map((item, index) => ({
        ...item,
        criterion_id: item.criterion_id || `${item.category || 'custom'}_${index + 1}`,
        criterion: item.criterion || item.label || '',
        label: item.label || item.criterion || '',
        category: item.category || 'custom',
        weight: Math.round(Number(item.weight || 0)),
        mandatory: Boolean(item.mandatory),
        source_jd_text: item.source_jd_text || item.criterion || item.label || '',
        expected_resume_evidence: item.expected_resume_evidence || item.criterion || item.label || '',
        sort_order: item.sort_order || index + 1,
      }))
    : []
);

const SliderIcon = () => (
  <svg className="w-5 h-5 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <line x1="4" y1="6" x2="20" y2="6" strokeLinecap="round" />
    <circle cx="9" cy="6" r="2" fill="white" stroke="currentColor" strokeWidth="1.5" />
    <line x1="4" y1="12" x2="20" y2="12" strokeLinecap="round" />
    <circle cx="15" cy="12" r="2" fill="white" stroke="currentColor" strokeWidth="1.5" />
    <line x1="4" y1="18" x2="20" y2="18" strokeLinecap="round" />
    <circle cx="10" cy="18" r="2" fill="white" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5h6v2M10 11v6M14 11v6M5 7l1 13h12l1-13" />
  </svg>
);

const JDScoreConfigModal = ({ open, jdId, jobTitle, onClose, onSaved, showCancelButton = true }) => {
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [source, setSource] = useState('company');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCriterion, setNewCriterion] = useState('');
  const [newWeight, setNewWeight] = useState('');
  const [addError, setAddError] = useState('');

  const encodedJdId = useMemo(() => encodeURIComponent(String(jdId || '').trim()), [jdId]);
  const displayJobTitle = String(jobTitle || '').trim();
  const total = totalScore(criteria);
  const totalOk = Math.abs(total - 100) <= 0.5;

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const loadConfig = useCallback(async () => {
    if (!open) return;
    if (!encodedJdId) {
      setCriteria([]);
      setLoading(false);
      showToast('error', 'Session id is missing for this configuration.');
      return;
    }
    setLoading(true);
    try {
      const data = await aiAutomationApi.get(`/score-config/jd-criteria/${encodedJdId}`);
      setCriteria(normalizeCriteria(data.criteria));
      setSource(data.source || 'company');
    } catch (error) {
      showToast('error', error?.detail || error?.message || 'Failed to load score criteria.');
    } finally {
      setLoading(false);
    }
  }, [encodedJdId, open]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  if (!open) return null;

  const updateCriterion = (index, field, value) => {
    setCriteria((previous) => {
      const next = [...previous];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAdd = (event) => {
    event.preventDefault();
    const label = newCriterion.trim();
    setAddError('');
    if (!label) {
      setAddError('Criterion is required.');
      return;
    }
    if (criteria.some((item) => String(item.criterion || '').trim().toLowerCase() === label.toLowerCase())) {
      setAddError('This criterion already exists.');
      return;
    }
    setCriteria((previous) => [
      ...previous,
      {
        criterion_id: `custom_${Date.now()}`,
        criterion: label,
        label,
        category: 'custom',
        weight: clamp(newWeight),
        mandatory: false,
        source_jd_text: label,
        expected_resume_evidence: label,
        sort_order: previous.length + 1,
      },
    ]);
    setNewCriterion('');
    setNewWeight('');
    setShowAddForm(false);
  };

  const handleSave = async () => {
    if (!totalOk) {
      showToast('error', `Criteria scores must total 100%. Current total is ${total}%.`);
      return;
    }
    setSaving(true);
    try {
      const payloadCriteria = criteria.map((item, index) => ({
        criterion_id: item.criterion_id || `${item.category || 'custom'}_${index + 1}`,
        criterion: item.criterion || item.label,
        label: item.label || item.criterion,
        category: item.category || 'custom',
        weight: Math.round(Number(item.weight || 0)),
        mandatory: Boolean(item.mandatory),
        source_jd_text: item.source_jd_text || item.criterion || item.label,
        expected_resume_evidence: item.expected_resume_evidence || item.criterion || item.label || '',
        validation_flag: item.validation_flag || '',
        sort_order: item.sort_order || index + 1,
      }));
      const data = await aiAutomationApi.put(`/score-config/jd-criteria/${encodedJdId}`, { criteria: payloadCriteria });
      const rescored = Number(data?.rescored_count || 0);
      const rescoreQueued = data?.rescore_status === 'queued';
      setSource('stored');
      showToast(
        'success',
        rescoreQueued
          ? 'Score criteria saved. Recalculation is running in the background.'
          : rescored > 0
            ? `Score criteria saved. Rescored ${rescored} candidates.`
            : 'Score criteria saved.'
      );
      onSaved?.();
      onClose?.();
    } catch (error) {
      showToast('error', error?.detail || error?.message || 'Failed to save score criteria.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 p-4">
      <div
        className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
              <SliderIcon />
              {displayJobTitle ? `${displayJobTitle} - Score Criteria` : 'Score Criteria'}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
              {source === 'stored' ? 'Review or edit the saved criteria for this JD.' : 'Confirm these criteria before candidate scoring.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-gray-200 text-slate-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Close score criteria"
          >
            ×
          </button>
        </div>

        <div className="max-h-[calc(88vh-145px)] overflow-y-auto px-6 py-5">
          <div className="mb-5 flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500 dark:text-gray-400">
              Criteria scores must total <span className="font-semibold text-slate-700 dark:text-gray-200">100%</span>.
            </p>
            <button
              onClick={() => { setShowAddForm((value) => !value); setAddError(''); }}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-800"
            >
              {showAddForm ? 'Cancel' : '+ Add Criterion'}
            </button>
          </div>

          {toast && (
            <div className={`mb-4 rounded-lg border px-4 py-3 text-sm font-medium ${
              toast.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200'
            }`}>
              {toast.msg}
            </div>
          )}

          {showAddForm && (
            <form onSubmit={handleAdd} className="mb-5 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 dark:border-indigo-700 dark:bg-indigo-900/20">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_auto] sm:items-end">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500 dark:text-gray-400">Criteria</span>
                  <input
                    type="text"
                    value={newCriterion}
                    onChange={(event) => setNewCriterion(event.target.value)}
                    placeholder="e.g. Python"
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-slate-500 dark:text-gray-400">Score %</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newWeight}
                    onChange={(event) => setNewWeight(event.target.value === '' ? '' : clamp(event.target.value))}
                    onBlur={() => setNewWeight(clamp(newWeight))}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </label>
                <button type="submit" className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700">
                  Add
                </button>
              </div>
              {addError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{addError}</p>}
            </form>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-400 dark:text-gray-500">Loading score criteria...</div>
          ) : criteria.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-slate-400 dark:text-gray-500">No score criteria found. Add one above.</div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-[minmax(520px,1fr)_120px_260px_38px] gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                <span>Criteria</span>
                <span className="text-center">Score</span>
                <span>Progress</span>
                <span />
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {criteria.map((item, index) => {
                  const weight = clamp(item.weight);
                  return (
                    <div key={item.criterion_id || `${item.criterion}_${index}`} className="grid grid-cols-[minmax(520px,1fr)_120px_260px_38px] items-center gap-3 px-4 py-3">
                      <input
                        type="text"
                        value={item.criterion}
                        onChange={(event) => {
                          const value = event.target.value;
                          setCriteria((previous) => {
                            const next = [...previous];
                            next[index] = {
                              ...next[index],
                              criterion: value,
                              label: value,
                              source_jd_text: value,
                              expected_resume_evidence: value,
                            };
                            return next;
                          });
                        }}
                        className="min-w-0 rounded-lg border border-transparent bg-transparent px-3 py-2 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-300 focus:bg-white dark:text-gray-200 dark:focus:border-indigo-700 dark:focus:bg-gray-800"
                      />
                      <div className="flex items-center justify-center gap-1">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={weightInputValue(item.weight)}
                          onChange={(event) => updateCriterion(index, 'weight', event.target.value === '' ? '' : clamp(event.target.value))}
                          onBlur={() => updateCriterion(index, 'weight', clamp(item.weight))}
                          className="w-16 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-center text-sm font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                        />
                        <span className="text-xs text-slate-400">%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={weight}
                          onChange={(event) => updateCriterion(index, 'weight', clamp(event.target.value))}
                          className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-indigo-600 dark:bg-gray-700"
                          style={{
                            background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${weight}%, #e5e7eb ${weight}%, #e5e7eb 100%)`,
                          }}
                        />
                        <span className="w-10 text-right text-xs font-semibold text-slate-500 dark:text-gray-400">{weight}%</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCriteria((previous) => previous.filter((_, itemIndex) => itemIndex !== index))}
                        title="Remove criterion"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && criteria.length > 0 && (
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-gray-300">Total score</span>
                <span className={`text-sm font-bold ${totalOk ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                  {total}% / 100%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${totalOk ? 'bg-green-500' : total > 100 ? 'bg-red-500' : 'bg-amber-400'}`}
                  style={{ width: `${Math.min(total, 100)}%` }}
                />
              </div>
              {!totalOk && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {total > 100 ? `Over by ${total - 100}%` : `Under by ${100 - total}%`} - adjust scores before saving.
                </p>
              )}
            </div>
          )}
        </div>

        {!loading && criteria.length > 0 && (
          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {showCancelButton && (
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || !totalOk}
              className="rounded-lg bg-indigo-600 px-8 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JDScoreConfigModal;
